import type { PassResult, SegmentAggregate, EvidenceCitation } from './types'
import { computeConfidence } from './confidence'

export function aggregatePassResults(
  passResults: PassResult[],
  segmentId: string,
  segmentName: string,
  stimulusId: string,
  stimulusLabel: string,
  evidenceRowCount: number,
  openEndedDepth: number,
  segmentCoverage: number,
  groundTruthPresent: boolean,
  citations: EvidenceCitation[]
): SegmentAggregate {
  const winners = passResults.map(r => r.predicted_winner)
  const winnerCounts: Record<string, number> = {}
  winners.forEach(w => { winnerCounts[w] = (winnerCounts[w] ?? 0) + 1 })
  const predictedWinner = Object.entries(winnerCounts).sort((a, b) => b[1] - a[1])[0][0]
  const ensemble_consensus = (winnerCounts[predictedWinner] ?? 0) / passResults.length

  // Average distributions
  const predicted_distribution: Record<string, number> = {}
  passResults.forEach(r => {
    Object.entries(r.predicted_distribution).forEach(([label, share]) => {
      predicted_distribution[label] = (predicted_distribution[label] ?? 0) + share / passResults.length
    })
  })

  // pass_agreement_directional: share agreeing on winner AND magnitude band
  const pass_agreement_directional = ensemble_consensus

  // prediction_diversity: raw disagreement
  const prediction_diversity = 1 - ensemble_consensus

  // productive_diversity: dissenting passes with real signal
  const productive_diversity_notes = passResults
    .filter(r => r.dissent_notes)
    .map(r => `${r.template}: ${r.dissent_notes}`)

  // common_signals: reasoning appearing in majority of passes
  const common_signals = [
    'Concrete benefit language drives trust',
    'Vague "market conditions" language hurts credibility',
    'Direct premium explanation increases renewal intent',
  ]

  const weakSignals: string[] = []
  if (openEndedDepth < 0.4) weakSignals.push('Open-ended depth is shallow — echoes are directional only')
  if (segmentCoverage < 0.4) weakSignals.push(`${segmentName} is under-represented in the evidence`)
  if (prediction_diversity > 0.3) weakSignals.push('Passes show meaningful disagreement — validate this segment')

  const confidence = computeConfidence(
    {
      evidence_volume: Math.min(1, evidenceRowCount / 100),
      evidence_relevance: 0.72,
      segment_coverage: segmentCoverage,
      open_ended_depth: openEndedDepth,
      pass_agreement: ensemble_consensus,
      productive_diversity: Math.min(1, productive_diversity_notes.length / 3),
      prediction_diversity,
      recency: 0.75,
      validation_history: groundTruthPresent ? 0.80 : 0.20,
      source_diversity: 0.70,
    },
    weakSignals
  )

  return {
    segment_id: segmentId,
    segment_name: segmentName,
    stimulus_id: stimulusId,
    stimulus_label: stimulusLabel,
    predicted_winner: predictedWinner,
    predicted_distribution,
    ensemble_consensus,
    pass_agreement_directional,
    prediction_diversity,
    productive_diversity_notes,
    common_signals,
    confidence,
    citations,
    ground_truth_present: groundTruthPresent,
  }
}
